import { HandleIcon } from 'components/icons';
import classes from 'classnames';
import React, { memo, useCallback } from 'react';
import ReactSlider from 'react-slider';
import styles from './Slider.module.scss';

export type Props = {
  disabled?: boolean;
  loading?: boolean;
  min: number;
  max: number;
  value?: number;
  heading?: string;
  label?: boolean;
  step?: number;
  units?: string;
  onChange?: (value: any) => void;
  onAfterChange?: (value: any) => void;
  dataTest?: string;
  className?: string;
};

const Slider = memo(function Slider({
  disabled = false,
  loading = false,
  min,
  max,
  value,
  heading,
  label,
  step,
  units,
  onChange,
  onAfterChange,
  dataTest = 'slider',
  className,
}: Props) {
  const renderThumb = useCallback(
    (props: Object) => {
      return (
        <div {...props} key={dataTest} data-test={`${dataTest}-thumb-layout`}>
          <div
            className={classes([
              styles.handle,
              loading
                ? styles['loading-handle']
                : styles['background-color-handle'],
            ])}
            data-test={`${dataTest}-handle`}
          >
            <HandleIcon
              className={styles['handle-icon']}
              data-test={`${dataTest}-handle-icon`}
            />
            <div
              className={classes(styles.tooltip)}
              data-test={`${dataTest}-tooltip`}
            >
              <div
                className={styles.amount}
                data-test="css-Edit-Request-Dialog"
              >
                <span data-test={`${dataTest}-thumb`} className={styles.value}>
                  {value}
                </span>
                {units && (
                  <span
                    className={styles.units}
                    data-test={`${dataTest}-units`}
                  >
                    {units}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      );
    },
    [loading, units, value],
  );

  return (
    <div data-test={dataTest} className={className}>
      <div className={styles.labels} data-test={`${dataTest}-labels`}>
        <span className={styles.heading} data-test={`${dataTest}-heading`}>
          {heading}
        </span>
        {label && (
          <span
            className={styles['amount-label']}
            data-test={`${dataTest}-labels-text`}
          >
            <span data-test={`${dataTest}-labels-value`}>{value}</span>
            {units && (
              <span
                className={styles.units}
                data-test={`${dataTest}-labels-units`}
              >
                {' '}
                {units}
              </span>
            )}
          </span>
        )}
      </div>
      <ReactSlider
        className={styles.slider}
        disabled={disabled}
        min={min}
        max={max}
        step={step}
        value={value}
        thumbClassName={styles.thumb}
        trackClassName={styles.track}
        renderThumb={renderThumb}
        onChange={onChange}
        onAfterChange={onAfterChange}
      />
      <div className={styles.limits} data-test={`${dataTest}-limits`}>
        <span data-test="xpath-Edit-Request-Min-Value">
          {min} {units}
        </span>
        <span data-test="xpath-Edit-Request-Max-Value">
          {max} {units}
        </span>
      </div>
    </div>
  );
});

export default Slider;
